import { Link } from 'react-router-dom';
import { Code, Palette, Layout, ShoppingCart, RefreshCw, Headphones, CheckCircle, ArrowRight } from 'lucide-react';

export const ServicesPage = () => {
  const services = [
    {
      icon: <Palette size={40} />,
      title: 'Web Design',
      description: 'Create stunning, user-friendly designs that capture your brand essence and convert visitors into customers.',
      timeline: '5-7 days',
      outcomes: [
        'Custom design mockups',
        'Mobile-responsive layouts',
        'Brand-aligned color schemes',
        'User experience optimization',
      ],
      included: [
        '2 design concepts',
        '2 rounds of revisions',
        'Mobile & desktop layouts',
        'Design system documentation',
      ],
      addons: ['Additional pages', 'Custom illustrations', 'Animation design', 'Brand identity package'],
    },
    {
      icon: <Code size={40} />,
      title: 'Web Development',
      description: 'Build fast, secure, and scalable websites using modern technologies and development best practices.',
      timeline: '7-14 days',
      outcomes: [
        'Clean, maintainable code',
        'Fast loading speeds',
        'SEO-optimized structure',
        'Cross-browser compatibility',
      ],
      included: [
        'Responsive development',
        'CMS integration',
        'Contact form setup',
        'Basic SEO implementation',
        'Google Analytics integration',
      ],
      addons: ['Custom functionality', 'API integrations', 'Advanced animations', 'Multi-language support'],
    },
    {
      icon: <Layout size={40} />,
      title: 'Landing Pages',
      description: 'High-converting landing pages designed to capture leads and drive specific actions for your campaigns.',
      timeline: '3-5 days',
      outcomes: [
        'Clear call-to-action',
        'Optimized conversion flow',
        'A/B testing ready',
        'Fast page load times',
      ],
      included: [
        'Single page design',
        'Lead capture form',
        'Mobile optimization',
        'Performance optimization',
      ],
      addons: ['Multiple variations', 'Advanced analytics', 'Exit-intent popups', 'Social proof integration'],
    },
    {
      icon: <ShoppingCart size={40} />,
      title: 'E-commerce (Shopify)',
      description: 'Complete online store solutions that make selling easy and provide seamless shopping experiences.',
      timeline: '14-21 days',
      outcomes: [
        'Secure checkout process',
        'Product catalog setup',
        'Payment gateway integration',
        'Inventory management',
      ],
      included: [
        'Shopify store setup',
        'Product page templates',
        'Shopping cart functionality',
        'Payment processor integration',
        'Shipping configuration',
      ],
      addons: ['Custom Shopify apps', 'Advanced filtering', 'Subscription products', 'Multi-currency support'],
    },
    {
      icon: <RefreshCw size={40} />,
      title: 'Website Redesign',
      description: 'Modernize your existing website with fresh design, improved performance, and better user experience.',
      timeline: '10-18 days',
      outcomes: [
        'Modern, updated look',
        'Improved site performance',
        'Better user engagement',
        'Enhanced mobile experience',
      ],
      included: [
        'Current site audit',
        'Redesign strategy',
        'Content migration',
        'SEO preservation',
        '301 redirects setup',
      ],
      addons: ['Content rewriting', 'New feature development', 'Conversion optimization', 'Brand refresh'],
    },
    {
      icon: <Headphones size={40} />,
      title: 'Maintenance & Support',
      description: 'Keep your website running smoothly with ongoing updates, security monitoring, and technical support.',
      timeline: 'Ongoing',
      outcomes: [
        'Website always up-to-date',
        'Security vulnerabilities patched',
        'Performance optimization',
        'Priority support access',
      ],
      included: [
        'Monthly updates',
        'Security monitoring',
        'Weekly backups',
        'Performance checks',
        'Content updates (up to 2 hours/month)',
      ],
      addons: ['Additional support hours', 'Emergency support', 'Analytics reporting', 'SEO management'],
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-32" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6" data-testid="services-hero-heading">
            Services That Drive Results
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-3xl mx-auto" data-testid="services-hero-description">
            From design to development, maintenance to marketing — we provide everything you need to succeed online.
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 md:py-32 bg-white" data-testid="services-detail-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-20">
            {services.map((service, index) => (
              <div
                key={index}
                className={`grid md:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'md:flex-row-reverse' : ''
                }`}
                data-testid={`service-detail-${index}`}
              >
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <div className="inline-flex p-4 bg-slate-50 rounded-2xl text-[#10B981] mb-6">
                    {service.icon}
                  </div>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-testid={`service-title-${index}`}>
                    {service.title}
                  </h2>
                  <p className="text-lg text-slate-600 mb-6" data-testid={`service-description-${index}`}>
                    {service.description}
                  </p>
                  <div className="inline-flex items-center gap-2 bg-[#10B981]/10 text-[#10B981] px-4 py-2 rounded-full font-medium mb-6">
                    <span className="font-mono text-sm" data-testid={`service-timeline-${index}`}>Timeline: {service.timeline}</span>
                  </div>

                  <div className="space-y-4 mb-6">
                    <h3 className="font-heading font-bold text-lg text-slate-900">Outcomes:</h3>
                    <ul className="space-y-2">
                      {service.outcomes.map((outcome, i) => (
                        <li key={i} className="flex items-start gap-2" data-testid={`service-outcome-${index}-${i}`}>
                          <CheckCircle size={20} className="text-[#10B981] mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
                    <h3 className="font-heading font-bold text-xl text-slate-900 mb-4">What's Included:</h3>
                    <ul className="space-y-3 mb-6">
                      {service.included.map((item, i) => (
                        <li key={i} className="flex items-start gap-2" data-testid={`service-included-${index}-${i}`}>
                          <CheckCircle size={18} className="text-[#10B981] mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{item}</span>
                        </li>
                      ))}
                    </ul>

                    <h3 className="font-heading font-bold text-lg text-slate-900 mb-3">Available Add-ons:</h3>
                    <div className="flex flex-wrap gap-2">
                      {service.addons.map((addon, i) => (
                        <span
                          key={i}
                          className="bg-white border border-slate-200 px-3 py-1.5 rounded-full text-sm text-slate-700"
                          data-testid={`service-addon-${index}-${i}`}
                        >
                          {addon}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="services-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6" data-testid="services-cta-heading">
            Not sure which service you need?
          </h2>
          <p className="text-lg text-slate-600 mb-8" data-testid="services-cta-description">
            Let's discuss your goals and find the perfect solution for your business.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#10B981] text-white hover:bg-[#059669] rounded-full px-8 py-4 font-bold shadow-lg shadow-green-500/20 hover:shadow-green-500/40 transition-all duration-300 hover:-translate-y-1 text-lg uppercase tracking-wide"
            data-testid="services-cta-button"
          >
            Get Free Consultation <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};
